import type { FC, ReactNode } from 'react'
import type { InventoryData } from '@/types'
import type { Lens } from '@/components/datacenter/store'

import dynamic from 'next/dynamic'
import { Component, useEffect, useState } from 'react'
import { AnimatePresence, MotionConfig, motion } from 'motion/react'
import { useDatacenterStore } from '@/components/datacenter/store'
import { InventoryProvider } from '@/components/datacenter/inventory-context'
import IntroOverlay from '@/components/datacenter/hud/intro-overlay'
import TopBar from '@/components/datacenter/hud/top-bar'
import ExploreDock from '@/components/datacenter/hud/explore-dock'
import ControlBar from '@/components/datacenter/hud/control-bar'
import DetailPanel from '@/components/datacenter/hud/detail-panel'
import InventoryDrawer from '@/components/datacenter/hud/inventory-drawer'
import Minimap from '@/components/datacenter/hud/minimap'
import Legend from '@/components/datacenter/hud/legend'
import Tour from '@/components/datacenter/hud/tour'
import Hints from '@/components/datacenter/hud/hints'

// The 3D hall needs WebGL and the browser, so it only ever renders on the client.
const DatacenterCanvas = dynamic(() => import('@/components/datacenter/scene/datacenter-canvas'), { ssr: false })

const LENS_CYCLE: Lens[] = ['standard', 'health', 'thermal']

interface CanvasBoundaryProps {
    children: ReactNode
}

// If 3D graphics cannot start, keep the rest of the interface usable.
class CanvasBoundary extends Component<CanvasBoundaryProps, { failed: boolean }> {
    state = { failed: false }

    static getDerivedStateFromError() {
        return { failed: true }
    }

    componentDidCatch(error: unknown) {
        console.error('The 3D view could not start:', error instanceof Error ? error.message : error)
        useDatacenterStore.getState().setSceneFailed(true)
    }

    render() {
        return this.state.failed ? null : this.props.children
    }
}

const supportsWebGL = (): boolean => {
    try {
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('webgl2') ?? canvas.getContext('webgl')

        context?.getExtension('WEBGL_lose_context')?.loseContext()

        return Boolean(context)
    }
    catch {
        return false
    }
}

const HallCanvas: FC<{ inventory: InventoryData }> = ({ inventory }): ReactNode => {
    const [supported, setSupported] = useState<boolean | null>(null)

    useEffect(() => {
        const ok = supportsWebGL()

        setSupported(ok)

        if (!ok) useDatacenterStore.getState().setSceneFailed(true)
    }, [])

    if (!supported) return null

    return (
        <CanvasBoundary>
            <DatacenterCanvas inventory={inventory} />
        </CanvasBoundary>
    )
}

const useKeyboardShortcuts = () => {
    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null
            const typing = Boolean(target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable))
            const state = useDatacenterStore.getState()

            if (event.key === 'Escape') {
                if (state.inventoryOpen) state.setInventoryOpen(false)
                else if (state.touring) state.stopTour()
                else if (state.phase === 'explore') state.back()

                return
            }

            if (typing || event.metaKey || event.ctrlKey || event.altKey) return

            if (state.phase === 'intro') {
                if (event.key === 'Enter' && state.sceneReady) state.enter()
                else if (event.key === 'Enter' && state.sceneFailed) state.skipIntro()

                return
            }

            if (state.phase !== 'explore') return

            switch (event.key) {
                case '1':
                    state.setFocus('overview')
                    break
                case '2':
                    state.setFocus('server')
                    break
                case '3':
                    state.setFocus('network')
                    break
                case '4':
                    state.setFocus('storage')
                    break
                case 'i':
                case 'I':
                    state.setInventoryOpen(!state.inventoryOpen)
                    break
                case '/':
                    event.preventDefault()
                    state.setInventoryOpen(true)
                    break
                case 'l':
                case 'L':
                    state.setLens(LENS_CYCLE[(LENS_CYCLE.indexOf(state.lens) + 1) % LENS_CYCLE.length])
                    break
                case 'g':
                case 'G':
                    if (state.touring) state.stopTour()
                    else state.startTour()
                    break
                case 'r':
                case 'R':
                    state.recenter()
                    break
                default:
                    break
            }
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [])
}

const useReducedMotionPreference = () => {
    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)')
        const sync = () => useDatacenterStore.getState().setReducedMotion(query.matches)

        sync()
        query.addEventListener('change', sync)

        return () => query.removeEventListener('change', sync)
    }, [])
}

interface DatacenterExperienceProps {
    inventory: InventoryData
    error: string | null
}

const DatacenterExperience: FC<DatacenterExperienceProps> = ({ inventory, error }): ReactNode => {
    const phase = useDatacenterStore(state => state.phase)

    useKeyboardShortcuts()
    useReducedMotionPreference()

    return (
        <InventoryProvider inventory={inventory}>
            <MotionConfig reducedMotion='user'>
                <div id='hall' className='relative h-dvh w-full overflow-hidden bg-[#04060b] text-slate-100'>
                    <div className='hall-backdrop absolute inset-0' aria-hidden='true' />

                    <div className='absolute inset-0'>
                        <HallCanvas inventory={inventory} />
                    </div>

                    <IntroOverlay error={error} />

                    <AnimatePresence>
                        {phase === 'explore' && (
                            <motion.div
                                key='hud'
                                className='pointer-events-none absolute inset-0'
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <TopBar />
                                <ExploreDock />
                                <ControlBar />
                                <Minimap />
                                <Legend />
                                <Hints />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <DetailPanel />
                    <Tour />
                    <InventoryDrawer />
                </div>
            </MotionConfig>
        </InventoryProvider>
    )
}

export default DatacenterExperience
